import { ReactNode } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import ChangeLine from '../../../components/novarium/ChangeLine'
import {
  OperatingRoomCancellationRowDTO,
  OperatingRoomCancellationsDTO,
  OperatingRoomOutcome,
} from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { percentVariation, Variation } from '../../ServicesStatistics/statisticsChange'
import { SectionState } from '../../UrgencySummary/useSectionData'
import { OperatingRoomDrill, OperatingRoomSelectFilters } from '../operatingRoomFilterModel'
import { formatCount, formatHours, NO_VALUE } from '../operatingRoomFormat'
import { absoluteVariation, previousPeriodRange } from '../operatingRoomVariation'
import Indicator from './OperatingRoomIndicatorCard'
import OperatingRoomSection from './OperatingRoomSection'
import OperatingRoomTable, { OperatingRoomTableColumn } from './OperatingRoomTable'

/** Las cuentas de una fila, en el orden de las columnas: cada una abre la lista con su estado. */
const COUNTS = [
  { key: 'completed', outcome: 'COMPLETED', headerId: 'OperatingRoomIndicatorsOutcomeCompleted' },
  { key: 'annulled', outcome: 'ANNULLED', headerId: 'OperatingRoomIndicatorsKpiAnnulled' },
  { key: 'rescheduled', outcome: 'RESCHEDULED', headerId: 'OperatingRoomIndicatorsKpiRescheduled' },
  { key: 'missed', outcome: 'MISSED', headerId: 'OperatingRoomIndicatorsKpiMissed' },
  { key: 'total', outcome: 'ALL', headerId: 'OperatingRoomIndicatorsCancellationsColTotal' },
] as const satisfies readonly { key: keyof OperatingRoomCancellationRowDTO; outcome: OperatingRoomOutcome; headerId: string }[]

type CountKey = (typeof COUNTS)[number]['key']

const HINT_ID: Partial<Record<CountKey, string>> = {
  rescheduled: 'OperatingRoomIndicatorsKpiRescheduledHint',
  total: 'OperatingRoomIndicatorsCancellationsColTotalHint',
}

interface OperatingRoomCancellationsProps {
  cancellations: SectionState<OperatingRoomCancellationsDTO>
  /** Un número abre la lista con las cirugías que cuenta (el estado y, en una fila, su sala o su tipo). */
  onDrill: (drill: OperatingRoomDrill) => void
}

/** «Con 4 anuladas sin fecha de anulación: …»: una frase más de la ⓘ, solo si hay (N > 0). */
function noteIf(intl: IntlShape, id: string, count: number): string {
  return count > 0 ? ` ${intl.formatMessage({ id }, { count, countText: formatCount(intl, count) })}` : ''
}

/**
 * Pestaña «Anulaciones»: anuladas, reprogramadas (una estimación, dicho en su ⓘ) y ausentes como tres cuentas separadas, nunca
 * sumadas en «canceladas», y cuánto antes del turno se anuló (mediana y p90 en horas, y cuántas con menos de 24 h o después del
 * turno). Debajo, las cuatro cuentas por quirófano y por tipo de cirugía. Cada indicador de cuenta y cada número de las tablas
 * abre la lista con ese estado (y con su sala o su tipo): el total de la lista es el número que se tocó. La anticipación no
 * tiene un filtro propio en la lista y no abre nada; un número en cero tampoco.
 */
export default function OperatingRoomCancellations({ cancellations, onDrill }: OperatingRoomCancellationsProps) {
  const intl = useIntl()
  return (
    <OperatingRoomSection
      label={intl.formatMessage({ id: 'OperatingRoomIndicatorsTabCancellations' })}
      state={cancellations}
      errorMessageId="OperatingRoomIndicatorsCancellationsError"
    >
      {(data) => <CancellationsContent data={data} onDrill={onDrill} />}
    </OperatingRoomSection>
  )
}

function CancellationsContent({ data, onDrill }: { data: OperatingRoomCancellationsDTO; onDrill: (drill: OperatingRoomDrill) => void }) {
  const intl = useIntl()
  const { current, previous, previousFrom, previousTo } = data.summary
  const period = previousPeriodRange(previousFrom, previousTo)
  const text = (id: string): string => intl.formatMessage({ id })

  const line = (variation: Variation | null): ReactNode =>
    (variation && period ? <ChangeLine variation={variation} period={period} /> : undefined)
  const countLine = (now: number, before: number): ReactNode => line(percentVariation(now, before))

  const { anticipation } = current
  const withDate = intl.formatMessage({ id: 'OperatingRoomIndicatorsCancellationsOfDated' }, { countText: formatCount(intl, anticipation.n) })

  /** Un número de una tabla: abre la lista con su estado y su fila; en cero, o sin fila a la que filtrar, es solo texto. */
  const countCell = (
    count: number,
    outcome: OperatingRoomOutcome,
    label: string,
    patch?: Partial<OperatingRoomSelectFilters>,
  ): ReactNode => (count === 0
    ? formatCount(intl, 0)
    : (
      <button
        type="button"
        className="nv-or-cell-drill"
        aria-label={label}
        onClick={() => onDrill({ outcome, ...(patch ? { patch } : {}) })}
      >
        {formatCount(intl, count)}
      </button>
    ))

  const columns = (
    nameHeaderId: string,
    rows: OperatingRoomCancellationRowDTO[],
    nameOf: (row: OperatingRoomCancellationRowDTO) => string,
    patchOf: (row: OperatingRoomCancellationRowDTO) => Partial<OperatingRoomSelectFilters> | null,
  ): OperatingRoomTableColumn<OperatingRoomCancellationRowDTO>[] => [
    {
      key: 'name',
      header: text(nameHeaderId),
      sortValue: nameOf,
      cell: nameOf,
    },
    ...COUNTS.map((count): OperatingRoomTableColumn<OperatingRoomCancellationRowDTO> => ({
      key: count.key,
      width: count.key === 'rescheduled' ? 176 : 136,
      header: text(count.headerId),
      hint: HINT_ID[count.key] ? text(HINT_ID[count.key]!) : undefined,
      centered: true,
      sortValue: (row) => row[count.key],
      cell: (row) => {
        const patch = patchOf(row)
        const label = intl.formatMessage({ id: 'OperatingRoomIndicatorsCancellationsViewOf' }, { status: text(count.headerId), name: nameOf(row) })
        // Sin sala o tipo (id nulo) no hay a qué filtrar: se ve el número, sin enlace
        return patch === null ? formatCount(intl, row[count.key]) : countCell(row[count.key], count.outcome, label, patch)
      },
      footer: countCell(
        rows.reduce((sum, row) => sum + row[count.key], 0),
        count.outcome,
        intl.formatMessage({ id: 'OperatingRoomIndicatorsCancellationsViewAll' }, { status: text(count.headerId) }),
      ),
    })),
  ]

  const roomName = (row: OperatingRoomCancellationRowDTO): string => row.name ?? text('OperatingRoomIndicatorsNoData')
  const typeName = (row: OperatingRoomCancellationRowDTO): string => (row.id === null ? text('OperatingRoomIndicatorsNoData') : row.name ?? NO_VALUE)

  return (
    <div className="nv-or-panel">
      <div className="nv-or-indicators-box">
        <div className="nv-or-indicators nv-or-indicators--six" role="group" aria-label={text('OperatingRoomIndicatorsCancellationsKpisLabel')}>
          <Indicator
            label={text('OperatingRoomIndicatorsKpiAnnulled')}
            hint={text('OperatingRoomIndicatorsKpiAnnulledHint')}
            value={formatCount(intl, current.annulled)}
            onDrill={current.annulled > 0 ? () => onDrill({ outcome: 'ANNULLED' }) : undefined}
            details={[countLine(current.annulled, previous.annulled)]}
          />
          <Indicator
            label={text('OperatingRoomIndicatorsKpiRescheduled')}
            hint={text('OperatingRoomIndicatorsKpiRescheduledHint')}
            value={formatCount(intl, current.rescheduled)}
            onDrill={current.rescheduled > 0 ? () => onDrill({ outcome: 'RESCHEDULED' }) : undefined}
            details={[countLine(current.rescheduled, previous.rescheduled)]}
          />
          <Indicator
            label={text('OperatingRoomIndicatorsKpiMissed')}
            hint={text('OperatingRoomIndicatorsKpiMissedHint')}
            value={formatCount(intl, current.missed)}
            onDrill={current.missed > 0 ? () => onDrill({ outcome: 'MISSED' }) : undefined}
            details={[countLine(current.missed, previous.missed)]}
          />
          <Indicator
            label={text('OperatingRoomIndicatorsCancellationsAnticipation')}
            hint={text('OperatingRoomIndicatorsCancellationsAnticipationHint')
              + noteIf(intl, 'OperatingRoomIndicatorsCancellationsNoteWithoutDate', data.dataNotes.annulledWithoutDate)}
            value={formatHours(intl, anticipation.medianHours)}
            details={[
              intl.formatMessage({ id: anticipation.n === 0 ? 'OperatingRoomIndicatorsTimesCasesOnly' : 'OperatingRoomIndicatorsCancellationsP90Cases' }, {
                p90: formatHours(intl, anticipation.p90Hours), count: anticipation.n, countText: formatCount(intl, anticipation.n),
              }),
              line(absoluteVariation(anticipation.medianHours, previous.anticipation.medianHours, (difference) => formatHours(intl, difference))),
            ]}
          />
          <Indicator
            label={text('OperatingRoomIndicatorsCancellationsUnder24')}
            hint={text('OperatingRoomIndicatorsCancellationsUnder24Hint')}
            value={formatCount(intl, anticipation.under24h)}
            details={[anticipation.n > 0 ? withDate : undefined, countLine(anticipation.under24h, previous.anticipation.under24h)]}
          />
          <Indicator
            label={text('OperatingRoomIndicatorsCancellationsAfterTurn')}
            hint={text('OperatingRoomIndicatorsCancellationsAfterTurnHint')}
            value={formatCount(intl, anticipation.afterTurn)}
            details={[anticipation.n > 0 ? withDate : undefined, countLine(anticipation.afterTurn, previous.anticipation.afterTurn)]}
          />
        </div>
      </div>

      <section className="nv-or-block" aria-labelledby="or-cancel-by-room">
        <h2 id="or-cancel-by-room" className="nv-section-title">{text('OperatingRoomIndicatorsCancellationsByRoom')}</h2>
        <OperatingRoomTable
          caption={text('OperatingRoomIndicatorsCancellationsByRoom')}
          rows={data.byRoom}
          rowKey={(row, index) => `${row.id ?? 'none'}-${index}`}
          columns={columns('OperatingRoomIndicatorsColRoom', data.byRoom, roomName, (row) => (row.id === null ? null : { roomIds: [row.id] }))}
          defaultSort={{ key: 'total', order: -1 }}
          footerLabel={text('OperatingRoomIndicatorsTotal')}
          emptyMessage={text('OperatingRoomIndicatorsCancellationsNoRows')}
        />
      </section>

      <section className="nv-or-block" aria-labelledby="or-cancel-by-type">
        <h2 id="or-cancel-by-type" className="nv-section-title">{text('OperatingRoomIndicatorsCancellationsByType')}</h2>
        <OperatingRoomTable
          caption={text('OperatingRoomIndicatorsCancellationsByType')}
          rows={data.byType}
          rowKey={(row, index) => `${row.id ?? 'none'}-${index}`}
          columns={columns('OperatingRoomIndicatorsColType', data.byType, typeName, (row) => (row.id === null ? null : { surgeryTypeId: row.id }))}
          defaultSort={{ key: 'total', order: -1 }}
          footerLabel={text('OperatingRoomIndicatorsTotal')}
          emptyMessage={text('OperatingRoomIndicatorsCancellationsNoRows')}
        />
      </section>
    </div>
  )
}
